// Lien de vérification d'identité (KYB Dotfile) d'un mandat en pending_kyb.
// Partagé entre le Dashboard et la page Conformité : l'URL est générée à la
// demande (elle expire en quelques jours) et ouverte dans un nouvel onglet.
// Au retour de l'utilisateur (focus de la fenêtre) et pendant les deux minutes
// qui suivent, /api/me est rechargé pour refléter la validation sans F5
// (constat preprod 2026-09-10 : le sandbox valide en 2 s, l'écran restait figé).
import { useEffect, useRef, useState } from 'react';
import { apiPost, ApiError } from './api';
import { useMe } from './useMe';

export interface KybNotice {
  title: string;
  body: string;
}

const REFRESH_DELAYS_MS = [5_000, 15_000, 40_000, 90_000];

export function useKybLink() {
  const { reload } = useMe();
  const [kybLoading, setKybLoading] = useState(false);
  // Accusé à afficher en modale (jamais de window.alert natif).
  const [kybNotice, setKybNotice] = useState<KybNotice | null>(null);
  const timersRef = useRef<number[]>([]);
  const armedRef = useRef(false);

  useEffect(() => {
    function onFocus() {
      if (armedRef.current) reload();
    }
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      timersRef.current.forEach((t) => window.clearTimeout(t));
    };
  }, [reload]);

  function armRefresh() {
    armedRef.current = true;
    timersRef.current.forEach((t) => window.clearTimeout(t));
    timersRef.current = REFRESH_DELAYS_MS.map((ms) => window.setTimeout(reload, ms));
  }

  async function openKybLink() {
    setKybLoading(true);
    // Onglet ouvert SYNCHRONEMENT au clic : après un await, Safari/Firefox bloquent
    // le popup (le geste utilisateur est consommé). L'URL est injectée à réception ;
    // opener coupé à la main (window.open avec 'noopener' renverrait null).
    const tab = window.open('about:blank', '_blank');
    if (tab) tab.opener = null;
    try {
      const res = await apiPost<{ url: string }>('/api/mandate/onboarding-link', {});
      armRefresh();
      if (tab) tab.location.href = res.url;
      else window.location.assign(res.url); // popup bloquée malgré tout : même onglet
    } catch (e) {
      tab?.close();
      if (e instanceof ApiError && e.code === 'KYB_NOT_PENDING') {
        // Le serveur a resynchronisé l'état depuis la plateforme : on recharge.
        reload();
        setKybNotice({
          title: 'Vérification déjà reçue',
          body: 'Votre dossier a déjà été transmis à la plateforme de facturation. Votre état de raccordement vient d’être mis à jour.',
        });
      } else {
        setKybNotice({
          title: 'Vérification indisponible',
          body: 'Le lien de vérification est indisponible pour le moment. Réessayez dans un instant.',
        });
      }
    } finally {
      setKybLoading(false);
    }
  }

  return { openKybLink, kybLoading, kybNotice, setKybNotice };
}

/** « 11 septembre 2026 » depuis 'YYYY-MM-DD' ; null si la date est inconnue. */
export function formatActivationDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}
